/**
 * window.__previsDebug — the Dev-only handle a debugger uses to READ and DRIVE the running app.
 *
 * 20260923 - WHY IT EXISTS. The runtime MCP server's `evaluate_javascript` tool (Dev-environment
 * player builds, localhost:9820) and Safari's Web Inspector both land in the page's global scope,
 * and until now there was nothing there to hold: every piece of state lives in a Zustand store that
 * is a module-local binding, so "what route is the board on, which shot is selected, is an overlay
 * swallowing Escape" could only be answered by reading the DOM and guessing. This puts the stores
 * and one JSON snapshot of the questions we actually ask on `window`, so a single eval answers them.
 *
 *   __previsDebug.state()                        the snapshot below — JSON-serialisable by contract,
 *                                                because evaluate_javascript returns JSON.stringify
 *   __previsDebug.stores.ui.getState()           the live stores themselves (the Zustand hooks, so
 *   __previsDebug.stores.project.setState({…})   getState / setState / subscribe all work)
 *   __previsDebug.call('escOverlayOpen')         a function some OTHER module registered — below
 *
 * THE REGISTRY is how a lazily loaded module contributes without this file importing it. The rule
 * is that this module imports the stores, capabilities and env ONLY — never a component — because
 * main.tsx installs it before createRoot, and a component import here would drag that component's
 * whole graph into the entry chunk, undoing the lazy() split App.tsx makes for it. So the module
 * that owns a predicate registers it into the hook as it evaluates, through `window` and a TYPE-only
 * import of this file, so neither module loads the other: SceneBoardPage registers `escOverlayOpen`
 * at module scope, and state() reports null for it until that chunk has loaded.
 *
 * DEV ONLY. Installed when the bundle resolved to Dev (`env.isDev` — the embedded bundle is built
 * with `npm run build`, i.e. Dev, unless Unity's Tools > Environment says Prod). A Prod bundle never
 * installs it: the stores can spend credits and rewrite the project, and a page global is reachable
 * by anything that runs in the page. The page globals it touches are typed HERE rather than in a
 * global declaration so that no production code can start leaning on them.
 */
import { env } from '@/config/environment';
import { capabilities } from '@/capabilities';
import { useUiStore } from '@/store/uiStore';
import type { WorkspaceRoute } from '@/store/uiStore';
import { useProjectStore } from '@/store/projectStore';
import { useAccountStore } from '@/store/accountStore';
import { usePlaybackStore } from '@/store/playbackStore';
import {
  useTutorialStore, selectAnyTourActive, selectInTutorialProject, selectInExampleProject,
} from '@/store/tutorialStore';
import type { TutorialKind, TutorialStep } from '@/store/tutorialStore';
import { useScreenplayImportStore } from '@/store/screenplayImportStore';

type RegisteredFn = (...a: unknown[]) => unknown;

/** The name SceneBoardPage registers its Escape-swallow predicate under (it passes the literal: it
 *  holds only a TYPE import of this module, so a runtime constant from here would not reach it). */
const ESC_OVERLAY_OPEN = 'escOverlayOpen';

type AccountBalance = ReturnType<typeof useAccountStore.getState>['accountBalance'];

export interface PrevisDebugStateSnapshot {
  /** uiStore.workspaceRoute — the scene-board shell's Script / Boards / Timeline tab. */
  route: WorkspaceRoute;
  selectedShotId: number | null;
  activeSceneId: number | null;
  readOnly: boolean;
  projectFileName: string;
  /** null while no project is loaded. */
  projectTitle: string | null;
  sceneCount: number;
  boardSelectMode: boolean;
  /** Every uiStore key ending in `Open` or `Mode` whose value is exactly `true` — the boolean
   *  dialogs, panels and modes that are up right now. String-valued modes (quickConfirmMode,
   *  sceneBoardViewMode, …) are deliberately not here; read them off `stores.ui`. */
  overlays: Record<string, true>;
  /** SceneBoardPage's "an overlay must swallow Escape" predicate, or null before that page's chunk
   *  has loaded (or if it threw). */
  escOverlayOpen: boolean | null;
  /** accountStore.accountBalance — null until the first balance fetch lands. */
  credits: AccountBalance;
  /** `active` is the tutorial store's "a tour owns the screen" (selectAnyTourActive). */
  tutorial: { active: boolean; kind: TutorialKind; step: TutorialStep; inTutorialProject: boolean; inExampleProject: boolean };
  /** A shallow copy of `capabilities` as it stands now (a debug harness can flip it at runtime). */
  capabilities: Record<string, boolean>;
  /** window.__previsAppPainted — the first frame has been composited (bridge/paintedFlag.ts). */
  painted: boolean;
}

export interface PrevisDebugHook {
  version: 1;
  /** The Zustand hooks themselves — `.getState()`, `.setState()` and `.subscribe()` all work. */
  stores: {
    ui: typeof useUiStore;
    project: typeof useProjectStore;
    account: typeof useAccountStore;
    playback: typeof usePlaybackStore;
    tutorial: typeof useTutorialStore;
    screenplayImport: typeof useScreenplayImportStore;
  };
  /** The live capabilities object (not a copy). */
  capabilities: typeof capabilities;
  env: { isDev: boolean; name: string };
  /** Registers (or, on an HMR re-evaluation, replaces) a named function for call() and state(). */
  register(name: string, fn: RegisteredFn): void;
  /** Calls a registered function; throws naming what IS registered when `name` is not. */
  call(name: string, ...args: unknown[]): unknown;
  state(): PrevisDebugStateSnapshot;
}

type DebugWindow = Window & { __previsDebug?: PrevisDebugHook; __previsAppPainted?: unknown };

function readEscOverlayOpen(registry: ReadonlyMap<string, RegisteredFn>): boolean | null {
  const fn = registry.get(ESC_OVERLAY_OPEN);
  if (!fn) return null;
  // A snapshot that throws answers nothing at all; one null field still answers the rest.
  try {
    return Boolean(fn());
  } catch (err) {
    console.warn('[previsDebug] escOverlayOpen threw:', err);
    return null;
  }
}

function snapshot(
  target: DebugWindow,
  registry: ReadonlyMap<string, RegisteredFn>,
): PrevisDebugStateSnapshot {
  const ui = useUiStore.getState();
  const ps = useProjectStore.getState();
  const tut = useTutorialStore.getState();
  const balance = useAccountStore.getState().accountBalance;

  const overlays: Record<string, true> = {};
  for (const [key, value] of Object.entries(ui)) {
    if (value === true && /(?:Open|Mode)$/.test(key)) overlays[key] = true;
  }

  return {
    route: ui.workspaceRoute,
    selectedShotId: ps.selectedShotId,
    activeSceneId: ps.activeSceneId,
    readOnly: ps.readOnly,
    projectFileName: ps.fileName,
    projectTitle: ps.project?.title ?? null,
    sceneCount: ps.project?.scenes?.length ?? 0,
    boardSelectMode: ui.boardSelectMode,
    overlays,
    escOverlayOpen: readEscOverlayOpen(registry),
    credits: balance ? { ...balance } : null,
    // 20260923 - through the store's selectors, never `.active`: "is a tour running" is two
    // questions since the Characters tour, and tutorialStore.activeReads.test.ts fails the build on
    // any direct read outside the store. selectAnyTourActive is the one a debugger means.
    tutorial: {
      active: selectAnyTourActive(tut),
      kind: tut.kind,
      step: tut.step,
      inTutorialProject: selectInTutorialProject(tut),
      inExampleProject: selectInExampleProject(tut),
    },
    capabilities: { ...capabilities },
    painted: target.__previsAppPainted === true,
  };
}

function createHook(target: DebugWindow): PrevisDebugHook {
  const registry = new Map<string, RegisteredFn>();
  return {
    version: 1,
    stores: {
      ui: useUiStore,
      project: useProjectStore,
      account: useAccountStore,
      playback: usePlaybackStore,
      tutorial: useTutorialStore,
      screenplayImport: useScreenplayImportStore,
    },
    capabilities,
    env: { isDev: env.isDev, name: env.name },
    register(name, fn) {
      if (typeof fn !== 'function') {
        throw new TypeError(`__previsDebug.register('${name}'): expected a function`);
      }
      registry.set(name, fn);
    },
    call(name, ...args) {
      const fn = registry.get(name);
      if (!fn) {
        const known = [...registry.keys()].join(', ') || 'none';
        throw new Error(`__previsDebug.call: nothing is registered as '${name}' (registered: ${known})`);
      }
      return fn(...args);
    },
    state: () => snapshot(target, registry),
  };
}

/**
 * Installs `window.__previsDebug` in a Dev bundle and returns it; returns null (and installs
 * nothing) otherwise. Idempotent: a second call returns the hook already there, so an HMR re-run of
 * main.tsx cannot orphan the functions lazily loaded modules registered into the first one.
 *
 * `opts.isDev` / `opts.target` exist for tests; main.tsx passes neither.
 */
export function installPrevisDebugHook(
  opts?: { isDev?: boolean; target?: Window },
): PrevisDebugHook | null {
  if (!(opts?.isDev ?? env.isDev)) return null;
  const target = (opts?.target ?? (typeof window !== 'undefined' ? window : undefined)) as
    DebugWindow | undefined;
  if (!target) return null;
  if (target.__previsDebug) return target.__previsDebug;
  const hook = createHook(target);
  target.__previsDebug = hook;
  return hook;
}
